/**
 * 🔐 Token Management Utilities for Grented Frontend
 *
 * 🔴 PRODUCTION DEPLOYMENT CHECKLIST:
 * ===================================
 * 1. Ensure VITE_API_BASE_URL is set to production API URL
 * 2. Remove localhost fallback for production builds
 * 3. Consider using secure HTTP-only cookies instead of localStorage for tokens
 * 4. Implement token refresh mechanism for production
 * 5. Add token expiration handling
 *
 * 📝 Required Environment Variables for Production:
 * - VITE_API_BASE_URL=https://api.yourdomain.com (NO localhost fallback in production)
 */

import { jwtDecode } from 'jwt-decode';

export interface JWTPayload {
  sub: string;
  email: string;
  role: string;
  exp: number;
  iat: number;
}

export function getApiBaseUrl(): string {
  // In sviluppo usiamo il proxy di Vite: ritorniamo stringa vuota
  // in modo che i chiamanti possano fare `${getApiBaseUrl()}/api/...` e ottenere '/api/...'
  const isDevelopment = import.meta.env?.DEV || import.meta.env?.MODE === 'development';
  if (isDevelopment) {
    return '';
  }

  // In produzione, usa VITE_API_BASE_URL; normalizziamo rimuovendo eventuali slash finali
  // e rimuovendo il suffisso '/api' se presente. I chiamanti continueranno a concatenare
  // '/api/...' in modo esplicito.
  const raw = import.meta.env?.VITE_API_BASE_URL || 'http://localhost:8000';
  let trimmed = raw.replace(/\/+$/, '');
  if (trimmed.endsWith('/api')) {
    trimmed = trimmed.slice(0, -4); // rimuove '/api'
  }
  return trimmed;
}

export function getToken(): string | null {
  return localStorage.getItem('access_token');
}

export function setToken(token: string) {
  localStorage.setItem('access_token', token);
}

export function removeToken() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('user');
}

export const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<JWTPayload>(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch {
    return true;
  }
};

export const getTokenPayload = (token: string): JWTPayload | null => {
  try {
    return jwtDecode<JWTPayload>(token);
  } catch {
    return null;
  }
};

export const getUserFromToken = (token: string) => {
  const payload = getTokenPayload(token);
  if (!payload) return null;

  return {
    id: payload.sub,
    email: payload.email,
    role: payload.role,
  };
};

/**
 * Build a full API URL for a given path. Ensures the base and the path are joined
 * correctly and that '/api' appears exactly once.
 */
export function apiUrl(path: string): string {
  const base = getApiBaseUrl();
  const trimmedBase = base.replace(/\/+$/, '');
  const trimmedPath = path.startsWith('/') ? path : `/${path}`;

  // If base is empty (dev), return the path prefixed with /api if needed
  if (!trimmedBase) {
    return trimmedPath.startsWith('/api') ? trimmedPath : `/api${trimmedPath}`;
  }

  // If the path already starts with /api, just join
  if (trimmedPath.startsWith('/api')) {
    return `${trimmedBase}${trimmedPath}`;
  }

  return `${trimmedBase}/api${trimmedPath}`;
}
